import {
  aiReelGeneratorURL,
  aiUgcVideoURL,
  aiVideoEditURL,
  avatarVideoProURL,
  facebookVideoAdURL,
  imageGenerateURL,
  videoGenerateURL,
  videoTranslateURL,
} from '@/utils/routeConstants';

import { SEOHeadProps } from '../components/SEOHead';
import { FAQItem } from '../components/templates/FAQSection';
import { FeatureItem } from '../components/templates/FeaturesSection';
import { StepData } from '../components/templates/HowToSection';
import { ToolItem } from '../components/templates/TikTokHeroSupportingSection';
import { ReasonItem } from '../components/templates/WhyChooseSection';
import { ScaleTabData } from '../components/UGCGenerator/UGCScaleSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createHowToSchema,
  createSoftwareAppSchema,
  createWebPageSchema,
} from '../utils/structuredData';

const PAGE_URL = 'https://falcocut.ai/dropshipping-video-ads-maker';
const PAGE_TITLE =
  'Dropshipping Video Ads Maker – Generate Winning Ecommerce Video Ads in Minutes';
const PAGE_DESCRIPTION =
  'Produce professional dropshipping ads for Shopify, TikTok, YouTube, and Meta in minutes. FalcoCut transforms product photos, scripts, or clips into conversion‑ready ecommerce videos—no editing skills needed.';

/** 首屏卡片图片配置 */
export const HERO_CARD_IMAGES: Record<string, { imageUrl: string; alt: string }> = {
  'ugc-video-ads': {
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/facebook-video-ads.png',
    alt: 'Dropshipping Video Ads',
  },
  'video-from-product-image': {
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/video-from-product-image.png',
    alt: 'Video from Product Image',
  },
  'generate-image': {
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/generate-image.png',
    alt: 'Generate Image',
  },
  'video-from-avatar-image': {
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/video-from-avatar-image.png',
    alt: 'Video from Avatar Image',
  },
};

/** Hero Section 配置 */
export const HERO_CONFIG = {
  title: 'Dropshipping Video Ads Maker',
  subtitle:
    'Generate studio‑quality dropshipping video ads with AI. FalcoCut turns product photos, scripts, and clips into ready‑to‑run ecommerce videos built to convert.',
};

/** Hero Supporting Section 配置 */
export const HERO_SUPPORTING_CONFIG = {
  title: 'What Kind of Dropshipping Video Ads Can FalcoCut Make',
  subtitle:
    'Build everything from high-energy TikTok product showcases to professional Facebook feed advertisements in just a few clicks. FalcoCut simplifies the entire production process, helping you launch and test new product creatives in record time.',
  ctaText: 'Create Dropshipping Video Ads',
};

export const HERO_SUPPORTING_TOOLS: ToolItem[] = [
  {
    title: 'Photo to Video',
    description:
      'Transform flat supplier photos into cinematic video sequences with professional motion, lighting effects, and immersive 3D environments.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/photo-to-video.png',
  },
  {
    title: 'Prompt to Video',
    description:
      'Convert your product descriptions or marketing scripts into complete video ads incorporating relevant visuals and professional narration.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/prompt-to-video.png',
  },
  {
    title: 'Product Avatar',
    description:
      'Upload or use custom avatars from our library to feature realistic digital presenters who demonstrate your products in action to build instant trust and social proof.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/product-avatar.png',
  },
];

/** How To Section 配置 */
export const HOW_TO_CONFIG = {
  title: 'How to Create Dropshipping Video Ads with AI',
  ctaText: 'Create Dropshipping Video Ads',
  imageUrl:
    'https://assets.falcocut.ai/dropshipping-video-ads-maker/how-to-create-dropshipping-video-ads-with-ai.png',
};

export const HOW_TO_STEPS: StepData[] = [
  {
    number: 1,
    title: 'Upload Your Product Details',
    description:
      'Add your product images, clips, or key selling points so the AI can understand what your ad should highlight.',
  },
  {
    number: 2,
    title: 'Choose a Video Avatar & Add a Script',
    description:
      'Select an AI presenter or upload your own footage, then add a script and choose a voice to define your message and delivery tone.',
  },
  {
    number: 3,
    title: 'Customize Your Video Layout',
    description:
      "Adjust aspect ratio, subtitle style, and background settings to match the platform where you'll run your dropshipping ads.",
  },
  {
    number: 4,
    title: 'Preview, Refine, and Export',
    description:
      'Review multiple AI‑generated versions, make quick edits if needed, and export the final ad ready for your ecommerce campaigns.',
  },
];

/** Features Section 配置 */
export const FEATURES_CONFIG = {
  title: 'Powerful Features for Better Dropshipping Video Ads',
  subtitle:
    'FalcoCut is designed to help you launch and scale high-performing dropshipping stores. Our integrated AI suite makes it easy to create professional video assets that capture attention and drive immediate sales on your e-commerce stores and platforms.',
  ctaText: 'Create Dropshipping Video Ads',
};

export const FEATURES_LIST: FeatureItem[] = [
  {
    title: 'Animate Supplier Photos into High-Energy Ads',
    description:
      'Turn flat supplier images into compelling video assets that look like professional studio productions. FalcoCut breathes life into static photography by adding dynamic camera sweeps and realistic environmental depth. You can launch professional Facebook campaigns before your first product sample even arrives. High-energy motion keeps viewers from scrolling past your offer in a crowded social media feed. Transforming basic assets into cinematic masterpieces has never been this fast or effortless for sellers.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/animate-supplier-photos-into-high-energy-ads.png',
    imageAlt: 'Animate Supplier Photos into High-Energy Ads',
    ctaText: 'Create Dropshipping Video Ads',
  },
  {
    title: 'Launch Rapid A/B Tests with AI Variations',
    description:
      'Create dozens of unique ad variations in seconds using our powerful text-to-video tools. Our AI-driven engine produces multiple video iterations so you can find a winning formula quickly. Testing different headlines and visual sequences becomes a seamless part of your daily store management. You can identify high-performing assets without spending hours inside a complicated video editing suite. Constant iteration ensures your pixel stays fed with fresh, engaging content that drives consistent sales.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/launch-rapid-a-b-tests-with-ai-variations.png',
    imageAlt: 'Launch Rapid A/B Tests with AI Variations',
    ctaText: 'Create Dropshipping Video Ads',
  },
  {
    title: 'Establish Trust Using Professional AI Avatars',
    description:
      'Bridge the gap between a cold store and a trusted brand by featuring realistic digital presenters in your ads. These lifelike avatars act as your dedicated creative team, delivering convincing product demos and testimonials. Having a "human" face on your landing page ads significantly increases conversion rates and customer confidence. You can skip the expensive influencer outreach and produce professional "talking head" content in minutes. It is the most scalable way to add a layer of authenticity to your dropshipping business.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/establish-trust-using-professional-ai-avatars.png',
    imageAlt: 'Establish Trust Using Professional AI Avatars',
    ctaText: 'Create Dropshipping Video Ads',
  },
  {
    title: 'Refine Every Frame with One-Click AI Editing',
    description:
      "Take total creative control over your marketing assets through our streamlined, browser-based editing interface. You can swap out uninspiring backgrounds for high-conversion settings that match your brand's specific aesthetic. Adding seasonal promotions, countdown timers, or custom watermarks takes only a few seconds of your time. FalcoCut eliminates the need for expensive freelancers or steep learning curves associated with professional software. Fine-tuning your video creative ensures that every frame is perfectly optimized for maximum click-through rates.",
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/refine-every-frame-with-one-click-ai-editing.png',
    imageAlt: 'Refine Every Frame with One-Click AI Editing',
    ctaText: 'Create Dropshipping Video Ads',
  },
  {
    title: 'Generate Global Voiceovers and Smart Captions',
    description:
      'Scale your winning products into international territories without the high cost of professional translation services. Our advanced AI dubbing technology syncs your message perfectly in 30+ different global languages. Integrated smart captions ensure that mobile users can follow your sales pitch even with their volume muted. This localization strategy allows you to tap into untapped markets where ad costs are often lower. Providing a native shopping experience helps you build a truly global ecommerce empire from scratch.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/generate-global-voiceovers-and-smart-captions.png',
    imageAlt: 'Generate Global Voiceovers and Smart Captions',
    ctaText: 'Create Dropshipping Video Ads',
  },
  {
    title: 'Dominate All Platforms with Tailored Ad Formats',
    description:
      'Ensure your advertisements look perfect on TikTok, Pinterest, and YouTube with a single click of a button. FalcoCut automatically reconfigures your layout to suit vertical, square, or widescreen formats without losing visual quality. Take one successful idea and repurpose it for your Shopify store or Amazon listings that are supported by high-traffic social media video ads. You can confidently export your files knowing they meet the latest technical specs. Diversifying your traffic across eBay or Etsy is the safest way to protect and grow your revenue.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/dominate-all-platforms-with-tailored-ad-formats.png',
    imageAlt: 'Dominate All Platforms with Tailored Ad Formats',
    ctaText: 'Create Dropshipping Video Ads',
  },
];

/** Video Showcase Section 配置 */
export const EXAMPLE_VIDEOS_CONFIG = {
  title: 'The Powerhouse Behind Your Ecommerce Brand',
  subtitle:
    'FalcoCut serves as your all-in-one creative hub by bringing every essential AI tool together to simplify your daily marketing tasks. You can quickly turn your Shopify or Amazon product listings into viral TikTok hooks and professional Facebook video ads from a single dashboard.',
  ctaText: 'Create Dropshipping Video Ads',
};

export const EXAMPLE_VIDEOS = [
  {
    id: 'video-1',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-facemask.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 1',
  },
  {
    id: 'video-2',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-earbuds.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 2',
  },
  {
    id: 'video-3',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-outfit.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 3',
  },
  {
    id: 'video-4',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-cream.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 4',
  },
  {
    id: 'video-5',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-perfume.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 5',
  },
  {
    id: 'video-6',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-use-case-energy-drink.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 6',
  },
  {
    id: 'video-7',
    videoUrl:
      'https://assets.falcocut.ai/ai-ugc-video-generator/ugc-video-product-review-reaction.mp4',
    posterUrl: '',
    alt: 'Dropshipping Video Ad Example 7',
  },
];

/** Use Cases Section 配置 */
export const USE_CASES_CONFIG = {
  title: 'Scale Your Winning Products with Effective Ads',
  subtitle:
    'Empower your brand to keep up with the fast-paced demands of modern commerce by automating the entire content creation cycle. Easily maintain a consistent presence across every social feed and ecommerce marketplace with professional videos generated at an industrial scale.',
};

export const USE_CASES_TABS: ScaleTabData[] = [
  {
    key: 'viral-social-hooks',
    label: 'Viral Social Hooks',
    title: 'Viral Social Hooks',
    description:
      'Create rapid-fire, vertical videos perfectly optimized for the TikTok and Instagram Reels algorithms to grab attention instantly. Churn out dozens of high-energy hooks to ensure your brand stays relevant and trending in the fast-moving social feeds.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/viral-social-hooks.png',
    ctaText: 'Create Short-Form Videos',
    ctaUrl: '/ai-video-generator',
  },
  {
    key: 'marketplace-teasers',
    label: 'Marketplace Teasers',
    title: 'Marketplace Teasers',
    description:
      'Build high-quality product showcases designed to boost conversions and lower bounce rates on your Shopify, Amazon, or eBay listings. Give your Shopify or Amazon store a premium feel that turns casual browsers into confident, long-term customers.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/marketplace-teasers.png',
    ctaText: 'Try Product Avatar',
    ctaUrl: '/ai-product-avatar',
  },
  {
    key: 'global-retargeting-ads',
    label: 'Global Retargeting Ads',
    title: 'Global Retargeting Ads',
    description:
      'Localize your best-performing content for international Facebook and YouTube audiences using seamless AI dubbing and smart captioning. Retarget global customers in their native language to significantly increase your overall return on ad spend.',
    imageUrl:
      'https://assets.falcocut.ai/dropshipping-video-ads-maker/global-retargeting-ads.png',
    ctaText: 'Translate Video Ads',
    ctaUrl: '/video-translator',
  },
];

/** Why Choose Section 配置 */
export const WHY_CHOOSE_CONFIG = {
  title: 'Why Choose FalcoCut Dropshipping Video Ads Maker',
  subtitle:
    'Gain a decisive edge in the competitive e-commerce landscape by replacing slow, expensive production with lightning-fast AI innovation. FalcoCut lets you test more products, enter new markets, and scale winners faster than any traditional agency.',
  ctaText: 'Create Dropshipping Video Ads',
};

export const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Test Products Faster',
    description:
      'Create many different ad versions in minutes to find your best sellers quickly. This helps you stop wasting money on ads that do not bring sales.',
  },
  {
    title: 'Save More Money',
    description:
      'Stop paying for expensive video editors or high-priced camera equipment. Use our AI tools to build professional videos for a tiny fraction of the usual cost.',
  },
  {
    title: 'Sell to Everyone',
    description:
      'Translate your videos into many languages instantly to reach customers all over the world. Scaling your brand to new countries has never been this easy.',
  },
  {
    title: 'Stop the Scroll',
    description:
      'Make your ads look professional with exciting motion and clear visuals that grab attention. High-quality videos help you build trust and get more clicks today.',
  },
  {
    title: 'Easy for Beginners',
    description:
      'You do not need any editing skills to use our simple dashboard. Just upload your product info and let the AI handle the hard work for you.',
  },
  {
    title: 'Safe and Secure',
    description:
      'Protect your brand data with our secure platform that prioritizes your privacy at every step. We ensure your creative assets and product information remain completely safe.',
  },
];

/** FAQ Section 配置 */
export const FAQ_CONFIG = {
  title: 'Frequently Asked Questions',
};

export const FAQ_LIST: FAQItem[] = [
  {
    question: 'What is a dropshipping video ad?',
    answer:
      'A dropshipping video ad is a short promotional clip designed to showcase products from a supplier to potential customers. These videos usually highlight a specific problem and then demonstrate how the product provides a perfect solution for the viewer. They are optimized for social media platforms to grab attention quickly and drive traffic directly to an e-commerce store. Using high-energy visuals and clear calls to action helps these ads convert casual scrollers into paying customers very effectively.',
  },
  {
    question:
      'Do I need any special skills to use FalcoCut dropshipping video ads maker?',
    answer:
      'No. You do not need any professional video editing experience or technical design skills to create high-quality ads with FalcoCut. Our intuitive interface is built for beginners and busy entrepreneurs who want to generate professional content with just a few clicks. The AI handles all the complex tasks like syncing audio, adding transitions, and animating static images for your marketing campaigns. You simply need to provide your product details and let our smart technology build the perfect advertisement for your brand.',
  },
  {
    question: 'How long does it take to create a dropshipping video ad?',
    answer:
      'Creating a professional video advertisement takes only a few minutes from start to finish using our streamlined AI production tools. You can move from a simple product image to a fully edited video asset in less than 60 seconds. This speed allows you to test multiple creative angles every day without spending hours behind a complicated computer editing screen. Our system is designed to help you launch your marketing campaigns faster than any traditional video production method available today.',
  },
  {
    question: 'Can I use FalcoCut dropshipping video ads maker for free?',
    answer:
      'FalcoCut offers a free trial version that allows you to explore our powerful features and generate your first video ads. You can test the AI video generator and see the quality of our professional templates before committing to a plan. This ensures that every dropshipper has the opportunity to experience the speed and efficiency of our all-in-one creative suite. We provide various subscription levels to match the growth of your business as you begin to scale your marketing efforts.',
  },
  {
    question: 'Can I upload my product photo directly?',
    answer:
      'Yes, you can easily upload your existing product photos directly from your computer or mobile device into the FalcoCut dashboard. Our AI will automatically analyze your images and turn them into dynamic video scenes with realistic motion and cinematic effects. This feature is perfect for dropshippers who want to use supplier photos but need them to look more professional. You can even combine multiple images to create a comprehensive showcase that highlights every unique feature of your winning product.',
  },
  {
    question: 'Can I edit or customize the generated ads after creation?',
    answer:
      'Yes. You have full creative control to edit and customize every single element of your video after the AI generates it. Our built-in editor allows you to change text overlays, swap background music, and adjust the timing of different video clips. You can also add your own brand logo and custom call-to-action buttons to ensure the video matches your store. This flexibility means you can fine-tune your creative assets until they perfectly represent your brand identity and marketing goals.',
  },
  {
    question: 'What kinds of ad formats and video lengths are supported?',
    answer:
      'FalcoCut supports all major video formats including vertical, square, and widescreen layouts to fit every social media platform perfectly. You can choose specific video lengths ranging from fifteen-second quick hooks to longer one-minute detailed product demonstration videos. This variety ensures that your content is always optimized for the specific requirements of Shopify, Amazon, TikTok, Instagram, Facebook, and YouTube. Our platform makes it simple to repurpose a single creative idea into multiple different formats with just one click.',
  },
  {
    question: 'Can AI-generated video ads be used across multiple platforms?',
    answer:
      'Yes. Our AI-generated videos are fully optimized for use across every major social media platform and e-commerce marketplace on the web. You can confidently run these ads on TikTok, Facebook, and Instagram or even embed them directly into your Shopify store. Because our tool handles the technical specifications for each site, your videos will always look professional and high-quality everywhere. This multi-channel approach helps you reach a wider audience and increases the chances of finding your next winning product.',
  },
  {
    question: 'What are the most effective types of dropshipping video ads?',
    answer:
      'The most effective ads are usually fast-paced problem-solving videos, authentic user reviews, and high-energy product demonstrations that stop the scroll. Videos that feature a "human" element, like our AI avatars, tend to build much higher trust with potential customers. Using a strong hook in the first three seconds is essential for keeping viewers engaged throughout the entire video advertisement. Combining these elements with a clear offer will consistently drive the best results for your dropshipping or e-commerce business.',
  },
];

/** Explore Tools Section 配置 */
export interface ExploreToolTag {
  title: string;
  link: string;
}

export const EXPLORE_TOOLS: ExploreToolTag[] = [
  { title: 'AI Avatar Generator', link: avatarVideoProURL },
  { title: 'AI Video Editor', link: aiVideoEditURL },
  { title: 'AI Video Generator', link: videoGenerateURL },
  { title: 'AI Video Translator', link: videoTranslateURL },
  { title: 'AI Image Generator', link: imageGenerateURL },
  { title: 'AI UGC Video Generator', link: aiUgcVideoURL },
  { title: 'Facebook Video Ad Creator', link: facebookVideoAdURL },
  { title: 'AI Reel Generator', link: aiReelGeneratorURL },
];

/** Footer CTA Section 配置 */
export const FOOTER_CTA_CONFIG = {
  title: 'Create the Best Facebook Video Ads with AI',
  subtitle:
    'Ready to upgrade your video ad creation? Use FalcoCut to streamline production, reduce expenses, and maximize ROI across your Facebook marketing campaigns.',
  ctaText: 'Create Facebook Video Ads Now',
};

/** SEO 配置 */
export const SEO_CONFIG: SEOHeadProps = {
  title: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  url: PAGE_URL,
  publishedTime: '2026-03-24',
  modifiedTime: '2026-03-24',
  structuredData: [
    createSoftwareAppSchema({
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
    }),
    createWebPageSchema({
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      datePublished: '2026-03-24',
      dateModified: '2026-03-24',
    }),
    createFAQSchema({
      faqs: FAQ_LIST.map((f) => ({
        question: f.question,
        answer: f.answer || '',
      })),
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'Dropshipping Video Ads Maker', url: PAGE_URL },
      ],
    }),
    createHowToSchema({
      name: HOW_TO_CONFIG.title,
      steps: HOW_TO_STEPS.map((s) => ({ name: s.title, text: s.description })),
    }),
  ],
};
